import _ from 'underscore';

import { Api } from './api';
import { ApiResponsePlugin, ModelPlugin } from './api-plugins';
import SentryErrorsPlugin from './api-sentry-errors';
import AuthenticationPlugin from './api-authn';
import DupeRequestPlugin from './api-dupe-request-plugin';
import TracerPlugin from './api-tracer';

/**
 * ApiCrud is a more friendly base class for API endpoints. It provides a set of
 * default plugins with support for automatic Model serialization and
 * deserialization (serde) of requests and responses. It can also create all the
 * standard CRUD endpoints given a base URL.
 */
export class ApiCrud extends Api {
  model = null;
  defaultPlugins = [
    TracerPlugin,
    SentryErrorsPlugin,
    DupeRequestPlugin,
    ApiResponsePlugin,
    ModelPlugin,
    AuthenticationPlugin,
  ];

  defaultOpts = null;

  /**
   * Create new API with basic CRUD endpoints:
   *
   * * index() - List models
   * * read(id) - Read a single model
   * * create(data) - Create a new model
   * * update(id, data) - Update the given model
   * * delete(id) - Delete a model with `id`
   *
   * @param {Axios} axios Axios instance to use for this endpoint
   * @param {Class} model class this endpoint's actions are on
   * @param {Object} data to initialize CRUD endpoints
   * @param {Object} opts Extra options data
   */
  constructor(axios, model, data, opts) {
    super(axios);
    if (arguments.length === 2) {
      // support skipping model and passing only data.
      // the only benefit of using ApiCrud w/o a model vs Api directly
      // is getting the default plugins included automatically.
      // eslint-disable-next-line no-param-reassign
      [data, model] = [model, null];
    }
    this.model = model;
    this.defaultOpts = {
      model: this.model,
      plugins: this.defaultPlugins,
      ...opts,
    };
    if (_.isString(data)) {
      this.createCommonCrudEndpoints(data);
    } else if (_.isObject(data)) {
      this.createCustomEndpoints(data);
    }
  }

  /**
   * Adds the given custom endpoints to this API instance
   */
  add(endpoints) {
    this.createCustomEndpoints(endpoints);
  }

  /**
   * Create custom endpoints. See Api#createEndpoints for details on the data format.

   * @param {Object} data
   */
  createCustomEndpoints(data) {
    const endpoints = data;
    // merge defaultOpts into the given endpoint definitions
    Object.keys(endpoints).forEach((key) => {
      const v = endpoints[key];
      if (_.isString(v)) {
        // just a URL given, use our default opts
        endpoints[key] = [v, this.defaultOpts];
      } else if (_.isArray(v)) {
        // use custom opts in addition to our defaults
        if (v.length === 1) {
          v.push({});
        }
        v[1] = { ...this.defaultOpts, ...v[1] };
      }
    });
    this.createEndpoints(endpoints);
  }

  createCommonCrudEndpoints(base) {
    const opts = this.defaultOpts;
    this.createEndpoints({
      index: [base, opts],
      read: [`${base}/:id`, opts],
      create: [`POST ${base}`, opts],
      update: [`PUT ${base}/:id`, opts],
      delete: [`DELETE ${base}/:id`, opts],
    });
  }
}
